import React, { useCallback, useEffect, useRef } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Underline,
} from "lucide-react";

const SIZES = [
  { label: "Mic", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Mare", value: "5" },
];

function exec(command, value) {
  document.execCommand(command, false, value ?? undefined);
}

export default function RichTextEditor({ value = "", onChange, placeholder = "Scrie aici…" }) {
  const ref = useRef(null);

  const sync = useCallback(() => {
    if (!ref.current) return;
    onChange(ref.current.innerHTML);
  }, [onChange]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const run = (command, valueArg) => {
    ref.current?.focus();
    exec(command, valueArg);
    sync();
  };

  const insertLink = () => {
    const url = window.prompt("URL link (ex. /contact sau https://…)");
    if (!url) return;
    run("createLink", url.trim());
  };

  const insertImage = () => {
    const url = window.prompt("URL imagine (ex. /api/uploads/… sau https://…)");
    if (!url) return;
    run("insertImage", url.trim());
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 bg-slate-50/80 p-2">
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("bold")} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("italic")} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("underline")} title="Underline">
          <Underline className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("formatBlock", "h2")} title="Titlu H2">
          <Heading2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("formatBlock", "h3")} title="Titlu H3">
          <Heading3 className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("insertUnorderedList")} title="Listă">
          <List className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("insertOrderedList")} title="Listă numerotată">
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("justifyLeft")} title="Stânga">
          <AlignLeft className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("justifyCenter")} title="Centru">
          <AlignCenter className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={() => run("justifyRight")} title="Dreapta">
          <AlignRight className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={insertLink} title="Link">
          <Link2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded-md p-1.5 text-slate-600 hover:bg-white" onMouseDown={(e) => e.preventDefault()} onClick={insertImage} title="Imagine (URL)">
          <ImageIcon className="h-3.5 w-3.5" />
        </button>
        <select
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[11px] text-slate-700"
          defaultValue="3"
          onChange={(e) => run("fontSize", e.target.value)}
          title="Mărime text"
        >
          {SIZES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">
          Culoare
          <input
            type="color"
            className="h-5 w-8 cursor-pointer border-0 bg-transparent p-0"
            defaultValue="#3e2723"
            onChange={(e) => run("foreColor", e.target.value)}
          />
        </label>
      </div>
      <div
        ref={ref}
        className="min-h-[160px] px-3 py-3 text-sm leading-relaxed text-slate-800 outline-none [&_a]:text-[#3e2723] [&_a]:underline [&_h2]:text-lg [&_h2]:font-bold [&_h3]:text-base [&_h3]:font-semibold [&_img]:my-2 [&_img]:max-h-48 [&_img]:rounded-lg"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={sync}
        onBlur={sync}
      />
    </div>
  );
}
